import { Sankey, ResponsiveContainer } from 'recharts';
import { Box, Skeleton, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import { formatMoneyAmount } from '@occc-app/lib/currency';
import { EstimatedReimbursementsFragment } from '@occc/graphql';

import { scanDataEstimatedReimbursementsChartMessages } from './scan-data-estimated-reimbursements-chart.messages';
import {
  SANKEY_NODE_WIDTH,
  SANKEY_CHART_HEIGHT,
  SANKEY_MARGIN,
  MIN_SANKEY_VALUE,
  DEFAULT_TOTAL_VALUE,
  DEFAULT_MULTI_UNIT_VALUE,
  DEFAULT_LOYALTY_VALUE,
} from './scan-data-estimated-reimbursements-chart.constants';
import { SankeyNode } from './sankey-node.component';
import {
  SankeyNodeProps,
  ScanDataEstimatedReimbursementsData,
} from './scan-data-estimated-reimbursements-chart.types';

export type ScanDataEstimatedReimbursementsChartProps = {
  height?: number;
  loading: boolean;
  error: ApolloError | undefined;
  data: EstimatedReimbursementsFragment | undefined | null;
};

export const ScanDataEstimatedReimbursementsChart = ({
  height = SANKEY_CHART_HEIGHT,
  loading = false,
  error,
  data,
}: ScanDataEstimatedReimbursementsChartProps) => {
  const { palette } = useTheme();

  const estimatedReimbursementsData: ScanDataEstimatedReimbursementsData | null = useMemo(() => {
    if (!data) {
      return null;
    }

    return {
      multiUnit: data.multipackTotal,
      loyalty: data.loyaltyTotal,
    };
  }, [data]);

  const sankeyData = useMemo(() => {
    const total = estimatedReimbursementsData
      ? estimatedReimbursementsData.multiUnit + estimatedReimbursementsData.loyalty
      : 0;
    const multiUnit = estimatedReimbursementsData ? estimatedReimbursementsData.multiUnit : 0;
    const loyalty = estimatedReimbursementsData ? estimatedReimbursementsData.loyalty : 0;

    let totalValue: number;
    let multiUnitValue: number;
    let loyaltyValue: number;

    if (total === 0) {
      totalValue = DEFAULT_TOTAL_VALUE;
      multiUnitValue = DEFAULT_MULTI_UNIT_VALUE;
      loyaltyValue = DEFAULT_LOYALTY_VALUE;
    } else if (multiUnit === 0 || loyalty === 0) {
      multiUnitValue = multiUnit === 0 ? MIN_SANKEY_VALUE : multiUnit;
      loyaltyValue = loyalty === 0 ? MIN_SANKEY_VALUE : loyalty;
      totalValue = multiUnitValue + loyaltyValue;
    } else {
      totalValue = total;
      multiUnitValue = multiUnit;
      loyaltyValue = loyalty;
    }

    const getDisplayValue = (value: number) =>
      estimatedReimbursementsData ? formatMoneyAmount(value, { showCents: false }) : '-';

    return {
      nodes: [
        {
          name: scanDataEstimatedReimbursementsChartMessages.totalEstimatedReimbursements,
          value: totalValue,
          displayValue: getDisplayValue(total),
        },
        {
          name: scanDataEstimatedReimbursementsChartMessages.estimatedMultiUnit,
          value: multiUnitValue,
          displayValue: getDisplayValue(multiUnit),
        },
        {
          name: scanDataEstimatedReimbursementsChartMessages.estimatedLoyalty,
          value: loyaltyValue,
          displayValue: getDisplayValue(loyalty),
        },
      ],
      links: [
        {
          source: 0,
          target: 1,
          value: multiUnitValue,
        },
        {
          source: 0,
          target: 2,
          value: loyaltyValue,
        },
      ],
    };
  }, [estimatedReimbursementsData]);

  const containerSx: SxProps<Theme> = useMemo(
    () => ({
      width: '100%',
      height,
      mt: 1,
      '& svg': {
        overflow: 'visible',
      },
      ml: -0.25,
    }),
    [height],
  );

  const renderNode = useCallback((props: SankeyNodeProps) => {
    return <SankeyNode {...props} />;
  }, []);

  const sankeyLink = useMemo(
    () => ({
      stroke: palette.primary.light,
    }),
    [palette.primary.light],
  );

  if (loading && !data) {
    return <Skeleton variant='rectangular' width='100%' height={height} />;
  }

  if (error) {
    return (
      <Box p={4} height={height}>
        <Typography color='error'>Something went wrong. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <ResponsiveContainer width='65%' height='100%'>
        <Sankey
          data={sankeyData}
          nodeWidth={SANKEY_NODE_WIDTH}
          margin={SANKEY_MARGIN}
          link={sankeyLink}
          node={renderNode}
        />
      </ResponsiveContainer>
    </Box>
  );
};
